import React, { useEffect, useState, useContext } from 'react'
import { format } from 'date-fns'
import ReactTooltip from 'react-tooltip'
import { toast } from 'react-toastify'
import { FiPlusCircle } from 'react-icons/fi'
import { useHistory } from 'react-router-dom'

import { api, authHeaders } from '~/services/api'

import scrollUtils from '~/util/scroll'
import { stringToDate, getDateString, getDate } from '~/util/date'

import useQuery from '~/hooks/useQuery'

import BoxDadosBasicos from '~/boxes/EspacoMedico/DadosBasicos'
import BoxDadosPessoais from '~/boxes/EspacoMedico/DadosPessoais'
import BoxEndereco from '~/boxes/EspacoMedico/Endereco'
import BoxPagamento from '~/boxes/EspacoMedico/Pagamento'
import BoxContatos from '~/boxes/EspacoMedico/Contatos'
import BoxLocalTrabalho from '~/boxes/EspacoMedico/LocalTrabalho'
import BoxMediaSocial from '~/boxes/EspacoMedico/MediaSocial'
import BoxArquivos from '~/boxes/Arquivos'

import Modal from '~/components/Modal'
import GerenciamentoDocumentos from '~/components/GerenciamentoDocumentos'

import { loadTips } from '~/util/tip'
import { TipContext } from '~/contexts/TipContext'

import {
    Container,
    BoxDocumentos
} from './styles'

import { GlobalContext } from '~/contexts/GlobalContext'

export default function () {
    const { setCodigo } = useContext(TipContext)

    const { contato, upd_doc } = useQuery()
    const history = useHistory()

    const { user } = useContext(GlobalContext)

    const [pessoa, setPessoa] = useState(null)
    const [showModalDocumentos, setShowModalDocumentos] = useState(false)

    async function loadPessoa() {
        try {
            const pessoaId = user?.pessoa.id

            const response = await api.get(`pessoa/${pessoaId}`, authHeaders())

            if (response.associado.medico) {
                const crm = response.associado.pessoa.documentos.find(documento => documento.tipo_documento.descricao === 'CRM').identificador

                let CFMData
                let situacao

                try {
                    CFMData = await api.get(`consulta_cfm/PA/${crm}`)

                    const { descricao } = await api.get(`situacao_associacao/${CFMData.situacao}`)
                    situacao = descricao
                } catch (e) {
                    const crmDoc = response.associado.pessoa.documentos.find(doc => doc.tipo_documento.id === 3)

                    situacao = crmDoc?.extra?.situacao || 'Não definido'

                    const situacoes = await api.get('situacao_associacao')

                    CFMData = {
                        situacao: situacoes.find(sit => sit.descricao === situacao).id,
                        especialidade: response.associado.especialidades.map(esp => esp.descricao),
                        nome: response.nome
                    }
                }

                const { situacao: situacaoCodigo, especialidade } = CFMData

                const contribuicoesPendentes = await api.get('contribuicao', {
                    params: {
                        crm,
                        status: 0
                    },
                    ...authHeaders()
                })

                response.tipoPagamentoAprovado = response.associado.tipo_pagamento_aprovacao_status

                response.situacao = {
                    codigo: situacaoCodigo,
                    descricao: situacao,
                    icone: situacaoCodigo === 'A' ? 'FiCheckCircle' : 'FiXCircle'
                }

                response.associado.especialidades = !especialidade?.length ? [] : especialidade.map(espec => {
                    const [descricao, codigoRQE] = espec.split(' - ')

                    if (codigoRQE) {
                        const [, rqe] = codigoRQE.split(': ')

                        return {
                            descricao: `${descricao}${rqe ? ` - ${codigoRQE}` : ''}`
                        }
                    }

                    return { descricao }
                })

                response.locais_trabalho = [
                    ...response.associado.locais_trabalho.map(local => {
                        const {
                            rua, numero, complemento, bairro, cidade, uf, cep
                        } = local.endereco

                        return {
                            ...local,
                            nome_local_trabalho: local.nome,
                            contato: local.contato?.contato || '',
                            endereco: `${rua}, ${numero}, ${complemento} - ${bairro} - ${cidade} - ${uf} - ${cep}`,
                            editable: true
                        }
                    }),
                    ...response.associado.instituicoes.filter(instituicao => !instituicao.associado_instituicao.desconto).map(instituicao => ({
                        id: instituicao.id,
                        nome_local_trabalho: instituicao.razao_social,
                        endereco: '',
                        editable: false
                    }))
                ]

                const tipoPagamento = response?.associado?.tipo_pagamento
                const dadosBancarios = response?.associado?.dados_bancarios

                const dadosInstituicao = response?.associado?.instituicoes?.find(instituicao => instituicao.associado_instituicao.desconto)

                const dadosCartaoCredito = response?.associado?.dados_cartao_credito

                response.pagamento = {
                    tipo: tipoPagamento,
                    dados_bancarios: tipoPagamento?.id === 1 && dadosBancarios ? {
                        ...dadosBancarios,
                        instituicao: dadosBancarios.instituicao
                    } : null,
                    dados_instituicao: tipoPagamento?.id === 2 && dadosInstituicao ? {
                        ...dadosInstituicao,
                        matricula: dadosInstituicao.associado_instituicao.matricula
                    } : null,
                    dados_cartao_credito: dadosCartaoCredito
                }

                response.contribuicoes_pendentes = contribuicoesPendentes.length
            }

            response.socio = response.associado.status_associacao.id === 3

            response.documentos = response.associado.pessoa.documentos

            response.medias = response.associado.medias.map(media => ({
                id: media.associado_media_social.id,
                media_id: media.id,
                icone: media.icone,
                nome: media.nome,
                valor: media.associado_media_social.valor
            }))

            response.endereco = response.associado.pessoa.endereco

            const rgExtraData = response.documentos?.find(documento => documento.tipo_documento.id === 1)?.extra
            const rg_data_emissao = rgExtraData?.data_emissao || ''
            const rg_orgao_emissor = rgExtraData?.orgao_emissor || ''

            response.dados_pessoais = {
                cpf: response.documentos?.find(documento => documento.tipo_documento.id === 2)?.identificador || '',
                data_nascimento: response.data_nascimento ? format(stringToDate(response.data_nascimento), 'dd/MM/yyyy') : '',
                rg: response.documentos?.find(documento => documento.tipo_documento.id === 1)?.identificador || '',
                rg_data_emissao: rg_data_emissao ? format(stringToDate(`${rg_data_emissao} 00:00:00`), 'dd/MM/yyyy') : '',
                rg_orgao_emissor
            }

            response.contatos = [
                ...response.associado.pessoa.contato.map(cont => ({
                    ...cont,
                    icone: cont.tipo_contato.icone
                }))
            ]

            const nomes = response.nome.split(' ')
            response.nome_reduzido = `${nomes[0]} ${nomes.slice(1, nomes.length - 1).map(sobrenome => `${sobrenome[0]}. `).join(' ')}${nomes[nomes.length - 1]}`

            response.data_aposentadoria = response.associado.aposentado ? format(new Date(response.associado.aposentado), 'dd/MM/yyyy') : null

            response.instituicoes_aposentadoria = response.associado.instituicoes_aposentadoria?.map(inst => inst.nome_fantasia).join(', ')

            setPessoa(response)
        } catch (e) {
            toast.error('Erro no carregamento das informações.')
        }
    }

    useEffect(() => {
        scrollUtils.toTop()

        loadPessoa()
    }, [])

    useEffect(() => {
        if (contato) {
            setTimeout(() => {
                scrollUtils.toElement('.box-local-trabalho')
            }, 500)
        }
    }, [])

    useEffect(() => {
        setShowModalDocumentos(upd_doc && pessoa)
    }, [pessoa])

    useEffect(() => {
        loadTips(setCodigo, 'form_associado_espaco_medico')
    }, [])

    async function reload() {
        setPessoa(null)
        setShowModalDocumentos(false)

        await loadPessoa()
    }

    async function updateDocumentos(values) {
        try {
            await api.put(`documentos/${pessoa.id}`, {
                documentos: values.documentos
                    .map(doc => ({
                        arquivo: doc.arquivo,
                        identificador: doc.identificador,
                        extra: doc.validade ? {
                            validade: getDateString(getDate(doc.validade))
                        } : doc.dataEmissao && doc.orgaoEmissor ? {
                            data_emissao: getDateString(getDate(doc.dataEmissao)),
                            orgao_emissor: doc.orgaoEmissor
                        } : doc.orgaoEmissor ? { orgao_emissor: doc.orgaoEmissor }
                            : doc.dataEmissao ? {
                                data_emissao: getDateString(getDate(doc.dataEmissao))
                            } : null,
                        tipo_documento: doc.tipo_documento.value
                    }))
            }, authHeaders())

            toast.success('Documentos atualizados.')

            history.push('/associado/dados_cadastrais')
        } catch (e) {
            toast.error('Ocorreu um erro ao tentar atualizar os documentos.')
        }
    }

    return (
        <Container>

            <main className="animated fadeIn faster">

                <BoxDadosBasicos pessoa={pessoa} reload={reload} />

                <BoxDadosPessoais pessoa={pessoa} reload={reload} />

                <BoxEndereco pessoa={pessoa} reload={reload} />

                {user?.associado.medico ? (
                    <BoxPagamento
                        pessoa={pessoa}
                        reload={reload}
                        disableLabel={
                            !pessoa?.tipoPagamentoAprovado
                                ? 'Não é possível alterar a forma de pagamento, pois já há uma alteração em análise.'
                                : !pessoa?.pagamento?.tipo
                                    ? 'Não é possível alterar a forma de pagamento pois não há uma forma de pagamento definida.'
                                    : pessoa?.associado.status_associacao.id !== 3
                                        ? 'Não é possível alterar a forma de pagamento enquanto a situação da associação não for regularizada.'
                                        : 'Não é possível alterar a forma de pagamento enquanto houverem contribuições pendentes.'
                        }
                        enableEdit={pessoa?.associado.status_associacao.id === 3 && !pessoa?.contribuicoes_pendentes && pessoa?.tipoPagamentoAprovado}
                        disabled={!pessoa?.associado.medico}
                    />
                ) : (
                    <div />
                )}

                <BoxContatos pessoa={pessoa} reload={reload} />

                {user?.associado.medico && (
                    <BoxLocalTrabalho pessoa={pessoa} reload={reload} disabled={!pessoa?.associado.medico} />
                )}

                <BoxMediaSocial pessoa={pessoa} reload={reload} />

                <BoxDocumentos>
                    <h1>Documentos</h1>

                    <FiPlusCircle
                        data-tip="Adicionar"
                        data-for="tooltip"
                        onClick={() => { setShowModalDocumentos(true) }}
                    />

                    <BoxArquivos
                        title={null}
                        arquivos={pessoa?.documentos.map(doc => ({
                            ...doc,
                            link: doc.arquivo?.link || '',
                            nome: doc.tipo_documento.descricao
                        }))}
                    />
                </BoxDocumentos>
            </main>

            <ReactTooltip
                id="tooltip"
                place="top"
                effect="solid"
                backgroundColor="#fff"
                arrowColor="#fff"
                textColor="#424242"
                className="tooltip-default"
            />

            <Modal
                isOpen={showModalDocumentos}
                handleClose={() => { setShowModalDocumentos(false) }}
                title="Gerenciamento de documentos"
            >
                <GerenciamentoDocumentos
                    handleSubmit={updateDocumentos}
                    documentos={pessoa?.documentos.map(doc => ({
                        id: doc.id,
                        arquivo: doc.arquivo,
                        tipo_documento: doc.tipo_documento,
                        extra: doc.extra,
                        identificador: doc.identificador
                    }))}
                    enablePreview={false}
                    categoria={pessoa?.associado.medico ? 'medico' : 'academico'}
                />
            </Modal>

        </Container>
    )
}
